import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client'

class AmplitudeExperimentService {
  private client: ExperimentClient | null = null

  /**
   * Initializes the Experiment client and fetches flag variants for the user.
   * Must be called after Amplitude Analytics is initialized and the user ID is set.
   * Uses `initializeWithAmplitudeAnalytics` which automatically wires up exposure
   * tracking — this is what creates the [Experiment] <flag_key> user property in Amplitude.
   */
  public async initialize(): Promise<void> {
    const deploymentKey = import.meta.env
      .VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY

    if (!deploymentKey) {
      console.warn('AmplitudeExperimentService: No deployment key configured')
      return
    }

    try {
      this.client = Experiment.initializeWithAmplitudeAnalytics(deploymentKey, {
        serverZone: 'EU',
      })

      await this.client.fetch()
      console.log(
        '[AmplitudeExperiment] Initialized. Variants:',
        this.client.all(),
      )
    } catch (e) {
      console.warn('AmplitudeExperimentService: Initialization failed', e)
    }
  }

  /**
   * Tears down the Experiment client and clears cached variants.
   * Should be called when the user revokes consent.
   */
  public teardown(): void {
    this.client = null
  }

  /**
   * Returns the assigned variant value for a given flag key.
   * Returns null if the client is not initialized or no variant is assigned.
   */
  public variant(flagKey: string): string | null {
    return this.client?.variant(flagKey)?.value ?? null
  }
}

export default new AmplitudeExperimentService()
